import { Button, CheckCircle, Icon } from "@dust-tt/sparkle";
import type { ReactNode } from "react";

// How long a connected card stays up, so "Connected" can be read before the
// card gives way to the agent's reply.
export const connectedBeatMs = 1500;

// The card's calls to action, in the theme's bright action blue rather than
// the steel-blue accent, so they stand out inside a reply.
export const connectAppButtonClassName =
  "border-[var(--color-action-blue)] bg-[var(--color-action-blue)] text-white hover:bg-[var(--color-action-blue-hover)] dark:border-[var(--color-action-blue)] dark:bg-[var(--color-action-blue)]";

interface ConnectAppCardAction {
  label: string;
  onClick: () => void;
  isLoading?: boolean;
  disabled?: boolean;
}

interface ConnectAppCardProps {
  // The agent's own line above the card, so the ask reads as part of its reply.
  message: string;
  appName: string;
  visual?: ReactNode;
  // One line under the app name: what the button will do, or who has to act.
  detail: string;
  isConnected: boolean;
  error?: string | null;
  primary?: ConnectAppCardAction;
  // Shown next to `dismiss`, e.g. to start a stuck sign-in over.
  secondary?: ConnectAppCardAction;
  dismiss?: ConnectAppCardAction;
  // Inside the card under the app row, e.g. an optional second credential.
  extra?: ReactNode;
  children?: ReactNode;
}

export function ConnectAppCard({
  message,
  appName,
  visual,
  detail,
  isConnected,
  error,
  primary,
  secondary,
  dismiss,
  extra,
  children,
}: ConnectAppCardProps) {
  const footerActions = isConnected
    ? []
    : [secondary, dismiss].filter((action) => action !== undefined);

  return (
    <div className="my-3 flex flex-col gap-3">
      <p className="text-base text-foreground dark:text-foreground-night">
        {message}
      </p>
      <div className="flex w-full max-w-xl flex-col gap-4 rounded-2xl border border-[color-mix(in_srgb,var(--color-action-blue)_35%,transparent)] bg-background p-4 shadow-[0_0_32px_-10px_color-mix(in_srgb,var(--color-action-blue)_55%,transparent)] dark:border-[color-mix(in_srgb,var(--color-action-blue)_50%,transparent)] dark:bg-background-night">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            {visual}
            <div className="flex min-w-0 flex-col">
              <span className="text-base font-semibold text-foreground dark:text-foreground-night">
                {appName}
              </span>
              <span
                className={
                  error
                    ? "text-sm text-warning-600 dark:text-warning-400"
                    : "text-sm text-muted-foreground dark:text-muted-foreground-night"
                }
                aria-live="polite"
              >
                {isConnected ? "Connected" : (error ?? detail)}
              </span>
            </div>
          </div>
          {isConnected ? (
            <Icon
              visual={CheckCircle}
              size="md"
              className="text-success-500 dark:text-success-400"
            />
          ) : (
            primary && (
              <Button
                variant="highlight"
                size="md"
                label={primary.label}
                isLoading={primary.isLoading}
                disabled={primary.disabled}
                onClick={primary.onClick}
                className={`w-full sm:w-auto ${connectAppButtonClassName}`}
              />
            )
          )}
        </div>
        {extra}
        {!isConnected && children}
        {footerActions.length > 0 && (
          <div className="flex flex-wrap justify-end gap-2">
            {footerActions.map((action) => (
              <Button
                key={action.label}
                variant="outline"
                size="sm"
                label={action.label}
                isLoading={action.isLoading}
                disabled={action.disabled}
                onClick={action.onClick}
                className="border-foreground text-foreground dark:border-foreground-night dark:text-foreground-night"
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
